import AppTextField from "@/components/AppTextField";
import FormItem from "@/components/FormItem";
import NationAutoComplete from "@/components/NationAutoComplete";
import AppLoadingButton from "@/components/mui/LoadingButton";
import API from "@/constant/API";
import {getUserInfo} from "@/redux/slices/user";
import {dispatch} from "@/redux/store";
import axiosInstance from "@/utils/axios";
import {alpha, Box, Divider, Grid, InputAdornment, Stack, Typography} from "@mui/material";
import {useFormik} from "formik";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import * as Yup from "yup";
import TextFieldLimitLabel from "@/components/TextFieldLimitLabel";
import lodash from "lodash";
import {getShippingAddress} from "@/redux/slices/card";
import NationCodeAutoComplete from "@/components/NationCodeAutoComplete";
import NationSelect from "@/components/NationSelect";
import RpIcon from "@/components/RpIcon.jsx";
import { useTheme } from '@mui/material/styles';
import NationChooser from "@/components/NationChooser.jsx";

export default function  ShippingAddressForm({onSuccess}) {
  const {t} = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState(null);
  const [area, setArea] = useState(null);
  const [areaRequiredError, setAreaRequiredError] = useState(false);

  const {countries} = useSelector((state) => state.app);
  const {shippingAddress} = useSelector((state) => state.card);


  useEffect(() => {
    _initData();
  }, []);

  const _initData = () => {
    if (!shippingAddress) {
      dispatch(getShippingAddress());
    }
  }

  useEffect(() => {
    if (shippingAddress) {
      setValues(shippingAddress);
      setTimeout(() => validateForm(shippingAddress), 0);
    }
    if (shippingAddress?.receiveNation) {
      const _country = countries.find((country) => country.code === shippingAddress.receiveNation)
      _onCountryChange(_country);
      // validate form on initial values set
    }
    if (shippingAddress?.receiveAreaCode) {
      const _country = countries.find((country) => country.areaCode === shippingAddress.receiveAreaCode)
      _onAreaChange(_country);
    }
  }, [shippingAddress]);

  let yupShape = {
    receiveFirstName: Yup.string().trim().required("this_field_is_required"),
    receiveLastName: Yup.string().trim().required("this_field_is_required"),
    receiveMobile: Yup.string().trim().required("this_field_is_required"),
    receiveAddress: Yup.string().trim().required("this_field_is_required"),
    line2: Yup.string().trim().required("this_field_is_required"),
    receiveCity: Yup.string().trim().required("this_field_is_required"),
    receiveProvince: Yup.string().trim().required("this_field_is_required"),
    receivePostCode: Yup.string().trim().required("this_field_is_required"),
  };
  const validationSchema = Yup.object().shape(yupShape);

  const _onSave = async () => {
    if (!country || !area) {
      // todo: 没选择国家（由于有默认选中项，理论不存在），需要提示用户
      return;
    }

    setLoading(true);
    const params = {...values};
    params.receiveNation = country?.code;
    params.sn = shippingAddress?.sn;
    params.receiveAreaCode = area?.areaCode;

    try {
      await axiosInstance.post(API.SHIPPING_ADDRESS_SAVE, params);
      _onSuccess();
    } finally {
      setLoading(false);
    }
  };

  const _onSuccess = () => {
    dispatch(getShippingAddress());
    if (onSuccess) onSuccess();
  };

  const {
    errors,
    values,
    touched,
    setValues,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    validateForm,
  } = useFormik({
    initialValues: {
      receiveFirstName: "",
      receiveLastName: "",
      receiveMobile: "",
      receiveAddress: "",
      line2: "",
      receiveCity: "",
      receiveProvince: "",
      receivePostCode: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: _onSave,
  });

  const _onCountryChange = (record) => {
    setCountry(record);
  };
  const _onAreaChange = (record) => {
    setArea(record);
    // setAreaRequiredError(record == null);
  };

  const isFormValid = lodash.isEmpty(errors);

  const nameHandleInput = (e) => {
    e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
  };
  const addressHandleInput = (e) => {
    e.target.value = e.target.value.replace(/[^\x00-\x7F]/g, '');
  };

  return (
    <Box>
      <form className="form" onSubmit={handleSubmit}>
        <Stack direction={'column'} gap={'24px'}>
          <Stack direction="row" alignItems="center" sx={theme => {return {backgroundColor: alpha(theme.palette.warning.main, 0.4), padding: '8px 12px',}}}>
            <RpIcon icon="info-circle" size={18} color={theme.palette.warning.main} />
            <Typography sx={theme => {
              return {fontSize: theme.typography.pxToRem(12)};
            }} color="warning.dark" className="form-subtitle">
              {t("please_fill_in_the_information_in_english")}
            </Typography>
          </Stack>
          <Box>
            <AppTextField
              label={t("first_name")}
              fullWidth
              autoComplete="off"
              name="receiveFirstName"
              size="large"
              clearable
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => setFieldValue("firstname", "")}
              error={Boolean(touched.receiveFirstName && errors.receiveFirstName)}
              helperText={touched.receiveFirstName && t(errors.receiveFirstName)}
              inputProps={{
                maxLength: 10,
                onInput: nameHandleInput
              }}
              endAdornment={
                <TextFieldLimitLabel
                    current={values.receiveFirstName?.length ?? 0}
                    total={10}
                />
              }
              value={values.receiveFirstName}
            />
          </Box>
          <Box>
            <AppTextField
              label={t("last_name")}
              fullWidth
              autoComplete="off"
              name="receiveLastName"
              size="large"
              clearable
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => setFieldValue("lastname", "")}
              error={Boolean(touched.receiveLastName && errors.receiveLastName)}
              helperText={touched.receiveLastName && t(errors.receiveLastName)}
              inputProps={{
                maxLength: 10,
                onInput: nameHandleInput
              }}
              endAdornment={
                <TextFieldLimitLabel
                    current={values.receiveLastName?.length ?? 0}
                    total={10}
                />
              }
              value={values.receiveLastName}
            />
          </Box>
          <Box>
            <AppTextField
                label={t("phone_number")}
                fullWidth
                autoComplete="off"
                name="receiveMobile"
                size="large"
                clearable
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                onClear={() => setFieldValue("receiveMobile", "")}
                inputProps={{
                  maxLength: 27,
                }}
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <Stack direction="row" alignItems="center">
                          <NationSelect
                            sx={{paddingLeft: '0px !important'}}
                            showAreaCode={true} selected={area} isCompact={true} onSelect={_onAreaChange} variant="borderless" />
                          <Divider sx={{ height: '20px' }} orientation="vertical"  />
                        </Stack>
                      </InputAdornment>
                  )
                }}
                endAdornment={
                  <TextFieldLimitLabel
                      current={values.receiveMobile?.length ?? 0}
                      total={27}
                  />
                }
                error={Boolean(
                    touched.receiveMobile && errors.receiveMobile
                )}
                helperText={
                    touched.receiveMobile && t(errors.receiveMobile)
                }
                value={values.receiveMobile}
            />
          </Box>
          <Box>
            <FormItem label={t("country_region")}>
              <NationSelect showAreaCode={false} selected={country} onSelect={_onCountryChange} />
              {/*<NationAutoComplete*/}
              {/*  fullWidth*/}
              {/*  value={country}*/}
              {/*  filter={(country) => country?.isSupportBilling === 1}*/}
              {/*  onChange={_onCountryChange}*/}
              {/*/>*/}
            </FormItem>
          </Box>
          <Box>
            <AppTextField
              label={t("address_line1")}
              fullWidth
              autoComplete="off"
              name="receiveAddress"
              size="large"
              clearable
              variant="outlined"
              // onChange={handleChange}
              onChange={(e) => {
                let isValid = true
                if (e.target.value.length > 0) {
                  isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                }
                if (!isValid) {
                  return
                }
                setFieldValue('receiveAddress', e.target.value);
              }}
              onBlur={handleBlur}
              onClear={() => setFieldValue("receiveAddress", "")}
              error={Boolean(touched.receiveAddress && errors.receiveAddress)}
              helperText={touched.receiveAddress && t(errors.receiveAddress)}
              inputProps={{
                maxLength: 50,
                onInput:addressHandleInput
              }}
              endAdornment={
                <TextFieldLimitLabel
                    current={values.receiveAddress?.length ?? 0}
                    total={50}
                />
              }
              value={values.receiveAddress}
            />
          </Box>
          <Box>
            <AppTextField
              label={t("address_line2")}
              fullWidth
              autoComplete="off"
              name="line2"
              size="large"
              clearable
              variant="outlined"
              // onChange={handleChange}
              onChange={(e) => {
                let isValid = true
                if (e.target.value.length > 0) {
                  isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                }
                if (!isValid) {
                  return
                }
                setFieldValue('line2', e.target.value);
              }}
              onBlur={handleBlur}
              onClear={() => setFieldValue("line2", "")}
              error={Boolean(touched.line2 && errors.line2)}
              helperText={touched.line2 && t(errors.line2)}
              inputProps={{
                maxLength: 50,
                onInput:addressHandleInput
              }}
              endAdornment={
                <TextFieldLimitLabel
                    current={values.line2?.length ?? 0}
                    total={50}
                />
              }
              value={values.line2}
            />
          </Box>
          <Box>
            <AppTextField
                label={t("province_state")}
                fullWidth
                autoComplete="off"
                name="receiveProvince"
                size="large"
                clearable
                variant="outlined"
                // onChange={handleChange}
                onChange={(e) => {
                  let isValid = true
                  if (e.target.value.length > 0) {
                    isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                  }
                  if (!isValid) {
                    return
                  }
                  setFieldValue('receiveProvince', e.target.value);
                }}
                onBlur={handleBlur}
                onClear={() => setFieldValue("receiveProvince", "")}
                error={Boolean(touched.receiveProvince && errors.receiveProvince)}
                helperText={touched.receiveProvince && t(errors.receiveProvince)}
                inputProps={{
                  maxLength: 20,
                  onInput:addressHandleInput
                }}
                endAdornment={
                  <TextFieldLimitLabel
                      current={values.receiveProvince?.length ?? 0}
                      total={20}
                  />
                }
                value={values.receiveProvince}
            />
          </Box>
          <Box>
            <AppTextField
              label={t("city")}
              fullWidth
              autoComplete="off"
              name="receiveCity"
              size="large"
              clearable
              variant="outlined"
              // onChange={handleChange}
              onChange={(e) => {
                let isValid = true
                if (e.target.value.length > 0) {
                  isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                }
                if (!isValid) {
                  return
                }
                setFieldValue('receiveCity', e.target.value);
              }}
              onBlur={handleBlur}
              onClear={() => setFieldValue("receiveCity", "")}
              error={Boolean(touched.receiveCity && errors.receiveCity)}
              helperText={touched.receiveCity && t(errors.receiveCity)}
              inputProps={{
                maxLength: 20,
                onInput:addressHandleInput
              }}
              endAdornment={
                <TextFieldLimitLabel
                    current={values.receiveCity?.length ?? 0}
                    total={20}
                />
              }
              value={values.receiveCity}
            />
          </Box>
          <Box>
            <AppTextField
              label={t("postal_code")}
              fullWidth
              autoComplete="off"
              name="receivePostCode"
              size="large"
              clearable
              variant="outlined"
              // onChange={handleChange}
              onChange={(e) => {
                let isValid = true
                if (e.target.value.length > 0) {
                  isValid = /^[a-zA-Z0-9-\\s]+$/.test(e.target.value);
                }
                if (!isValid) {
                  return
                }
                setFieldValue('receivePostCode', e.target.value);
              }}
              onBlur={handleBlur}
              onClear={() => setFieldValue("receivePostCode", "")}
              error={Boolean(touched.receivePostCode && errors.receivePostCode)}
              helperText={touched.receivePostCode && t(errors.receivePostCode)}
              inputProps={{
                maxLength: 10,
                onInput:addressHandleInput
            }}
              endAdornment={
                <TextFieldLimitLabel
                    current={values.receivePostCode?.length ?? 0}
                    total={10}
                />
              }
              value={values.receivePostCode}
            />
          </Box>
          <Box>
            <AppLoadingButton
                type="submit"
                disabled={!isFormValid}
                fullWidth
                loading={loading}
                size="large"
                variant="contained"
            >
              {t("save")}
            </AppLoadingButton>
          </Box>
        </Stack>


      </form>
    </Box>
  );
}
